import { useNavigate } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

type ListItem = string | { bold: string; text: string };

type Block =
  | { type: "p"; text: string }
  | { type: "heading"; text: string }
  | { type: "list"; items: ListItem[] }
  | {
      type: "address";
      lines: string[];
      email: string;
      phone: string;
    };

const SECTIONS: { title: string; blocks: Block[] }[] = [
  {
    title: "1. Overview",
    blocks: [
      {
        type: "p",
        text: 'This Privacy Policy describes how Trevica LTD ("Company", "we", "our", or "us") collects, uses, discloses, and protects personal information when you visit our website, use our applications, or access any related services (collectively, the "Services").',
      },
      {
        type: "address",
        lines: ["Registered Address", "307 Cotton Exchange", "Old Hall Street", "Liverpool", "L3 9LQ", "United Kingdom"],
        email: "support@trevicatech.com",
        phone: "+44 7451 257027",
      },
      {
        type: "p",
        text: "Protecting your privacy is important to us. We are committed to handling personal information responsibly and in accordance with applicable data protection legislation, including the UK General Data Protection Regulation (UK GDPR), where applicable.",
      },
      {
        type: "p",
        text: "By using our Services, you acknowledge that you have read and understood this Privacy Policy.",
      },
    ],
  },
  {
    title: "2. Information We Collect",
    blocks: [
      {
        type: "p",
        text: "Depending on how you interact with the Services, we may collect the following categories of information.",
      },
      { type: "heading", text: "Personal Information" },
      { type: "p", text: "This may include:" },
      {
        type: "list",
        items: [
          "Name",
          "Email address",
          "Telephone number (where provided)",
          "Information you voluntarily submit through contact forms or customer support communications",
        ],
      },
      { type: "heading", text: "Device and Technical Information" },
      { type: "p", text: "We may automatically collect:" },
      {
        type: "list",
        items: [
          "IP address",
          "Browser type and version",
          "Operating system",
          "Device information",
          "Pages viewed",
          "Referral URLs",
          "Date, time, and duration of visits",
        ],
      },
      { type: "heading", text: "Communications" },
      { type: "p", text: "If you contact us, we may retain:" },
      {
        type: "list",
        items: [
          "Email correspondence",
          "Support requests",
          "Feedback or survey responses",
          "Other communications relating to our Services",
        ],
      },
      { type: "heading", text: "Cookies and Similar Technologies" },
      {
        type: "p",
        text: "We use cookies and comparable technologies to operate the website, improve performance, analyze traffic, and enhance your experience. More information is available in Section 9.",
      },
    ],
  },
  {
    title: "3. How We Use Personal Information",
    blocks: [
      { type: "p", text: "Your information may be used to:" },
      {
        type: "list",
        items: [
          "Provide, operate, and maintain our Services.",
          "Respond to inquiries and customer support requests.",
          "Improve website functionality and user experience.",
          "Monitor performance and identify technical issues.",
          "Protect the security and integrity of the Services.",
          "Detect, prevent, and investigate fraud or misuse.",
          "Comply with legal and regulatory obligations.",
          "Send service-related communications.",
          "Send marketing communications where permitted by law or where you have provided consent.",
        ],
      },
    ],
  },
  {
    title: "4. Legal Grounds for Processing",
    blocks: [
      {
        type: "p",
        text: "Where required by applicable law, we process personal information on one or more of the following legal bases:",
      },
      {
        type: "list",
        items: [
          "Your consent.",
          "Performance of a contract or steps taken before entering into a contract.",
          "Compliance with legal obligations.",
          "Our legitimate interests, provided those interests do not override your fundamental rights and freedoms.",
        ],
      },
    ],
  },
  {
    title: "5. Disclosure of Information",
    blocks: [
      { type: "p", text: "We may disclose personal information only where necessary." },
      { type: "heading", text: "Service Providers" },
      { type: "p", text: "We work with trusted third-party providers that assist us with:" },
      {
        type: "list",
        items: ["Website hosting", "Technical infrastructure", "Analytics", "Customer support", "Communication services"],
      },
      {
        type: "p",
        text: "These providers process information only on our instructions and under appropriate contractual safeguards.",
      },
      { type: "heading", text: "Legal Requirements" },
      { type: "p", text: "We may disclose information where required to:" },
      {
        type: "list",
        items: [
          "Comply with applicable laws or legal proceedings.",
          "Protect our legal rights.",
          "Prevent fraud or unlawful activity.",
          "Protect the safety of users or third parties.",
        ],
      },
      { type: "heading", text: "Business Transactions" },
      {
        type: "p",
        text: "If Trevica LTD undergoes a merger, acquisition, restructuring, or sale of assets, personal information may be transferred as part of that transaction, subject to appropriate confidentiality obligations.",
      },
      { type: "heading", text: "With Your Permission" },
      {
        type: "p",
        text: "We may share information with third parties where you have expressly authorized us to do so.",
      },
      { type: "p", text: "We do not sell or rent personal information to third parties." },
    ],
  },
  {
    title: "6. Data Retention",
    blocks: [
      { type: "p", text: "We retain personal information only for as long as reasonably necessary to:" },
      {
        type: "list",
        items: [
          "Deliver the requested Services.",
          "Meet legal, regulatory, accounting, or reporting obligations.",
          "Resolve disputes.",
          "Enforce our legal rights and agreements.",
        ],
      },
      {
        type: "p",
        text: "Retention periods vary depending on the type of information and the purpose for which it was collected.",
      },
    ],
  },
  {
    title: "7. Security Measures",
    blocks: [
      {
        type: "p",
        text: "We maintain appropriate technical and organizational safeguards designed to protect personal information against accidental loss, unauthorized access, disclosure, alteration, or destruction.",
      },
      { type: "p", text: "These safeguards may include:" },
      {
        type: "list",
        items: [
          "Secure servers and encrypted communications where appropriate.",
          "Restricted access to personal information.",
          "Internal security procedures and monitoring.",
          "Regular review of security practices.",
        ],
      },
      { type: "p", text: "While we take reasonable precautions, no online system can guarantee absolute security." },
    ],
  },
  {
    title: "8. Your Privacy Rights",
    blocks: [
      { type: "p", text: "Subject to applicable data protection laws, you may have the right to:" },
      {
        type: "list",
        items: [
          "Request access to your personal information.",
          "Correct inaccurate or incomplete information.",
          "Request deletion of your information.",
          "Restrict or object to certain processing activities.",
          "Request transfer of your information where applicable.",
          "Withdraw consent at any time where processing is based on consent.",
        ],
      },
      { type: "p", text: "To exercise your rights, please contact:" },
      { type: "p", text: "support@trevicatech.com" },
      { type: "p", text: "We will respond to requests within the timeframe required by applicable law." },
    ],
  },
  {
    title: "9. Cookies",
    blocks: [
      { type: "p", text: "Cookies help us provide a secure and efficient browsing experience." },
      { type: "p", text: "The Services may use:" },
      {
        type: "list",
        items: [
          { bold: "Essential Cookies", text: "— necessary for the operation of the website." },
          { bold: "Performance Cookies", text: "— help us understand how visitors use the website." },
          { bold: "Functional Cookies", text: "— remember user preferences and improve usability." },
        ],
      },
      {
        type: "p",
        text: "Most web browsers allow you to control or disable cookies through browser settings. Please note that some functionality may not operate correctly if certain cookies are disabled.",
      },
    ],
  },
  {
    title: "10. International Transfers",
    blocks: [
      {
        type: "p",
        text: "Where personal information is transferred outside your country of residence, we take appropriate measures to ensure that it remains protected in accordance with applicable data protection laws.",
      },
    ],
  },
  {
    title: "11. Children's Privacy",
    blocks: [
      { type: "p", text: "Our Services are intended for individuals aged 16 years or older." },
      {
        type: "p",
        text: "We do not knowingly collect personal information from children. If we learn that personal information has been collected from a child without appropriate authorization, we will take reasonable steps to delete it promptly.",
      },
    ],
  },
  {
    title: "12. Updates to this Privacy Policy",
    blocks: [
      {
        type: "p",
        text: "We may revise this Privacy Policy from time to time to reflect changes in our Services, business operations, or legal obligations.",
      },
      { type: "p", text: "The latest version will always be available on this page." },
      {
        type: "p",
        text: "Your continued use of the Services after any updates become effective constitutes acceptance of the revised Privacy Policy.",
      },
    ],
  },
  {
    title: "13. Contact Us",
    blocks: [
      {
        type: "p",
        text: "If you have questions regarding this Privacy Policy or our privacy practices, please contact us:",
      },
      {
        type: "address",
        lines: ["Trevica LTD", "307 Cotton Exchange", "Old Hall Street", "Liverpool", "L3 9LQ", "United Kingdom"],
        email: "support@trevicatech.com",
        phone: "+44 7451 257027",
      },
    ],
  },
];

function SectionBlock({ block }: { block: Block }) {
  if (block.type === "p") {
    return (
      <p className="text-sm md:text-base text-ink-700 leading-relaxed mt-3">
        {block.text}
      </p>
    );
  }

  if (block.type === "heading") {
    return (
      <h3 className="font-display font-semibold text-base md:text-lg text-ink-950 mt-5">
        {block.text}
      </h3>
    );
  }

  if (block.type === "list") {
    return (
      <ul className="mt-3 space-y-2 list-disc pl-5">
        {block.items.map((item, i) => (
          <li key={i} className="text-sm md:text-base text-ink-700 leading-relaxed">
            {typeof item === "string" ? (
              item
            ) : (
              <>
                <span className="font-semibold text-ink-950">{item.bold}</span> {item.text}
              </>
            )}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="mt-3 text-sm md:text-base text-ink-700 leading-relaxed">
      <p className="font-semibold text-ink-950">{block.lines[0]}</p>
      {block.lines.slice(1).map((line, i) => (
        <p key={i}>{line}</p>
      ))}
      <p className="mt-2">
        Email: <span className="font-medium text-ink-950">{block.email}</span>
      </p>
      <p>
        Phone: <span className="font-medium text-ink-950">{block.phone}</span>
      </p>
    </div>
  );
}

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream-50 font-sans text-ink-950">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden bg-cream-100 border-b border-ink-950/5">
          <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-14">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-700 hover:text-ink-950 transition-colors cursor-pointer mb-6"
            >
              <i className="ri-arrow-left-line text-base"></i>
              Back
            </button>
            <p className="text-xs md:text-sm font-medium uppercase tracking-[0.2em] text-ink-500 mb-4">
              Last updated: July 2026
            </p>
            <h1 className="font-display font-bold text-ink-950 leading-[1.08] tracking-tight text-3xl md:text-4xl lg:text-5xl">
              Privacy Policy
            </h1>
            <p className="mt-4 text-sm md:text-base text-ink-700 leading-relaxed max-w-2xl">
              Your privacy matters to us. This policy explains what data we collect, how we use it, and the rights you have over your personal information.
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="relative w-full py-16 md:py-24 bg-cream-50">
          <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-14">
            <div className="space-y-10 md:space-y-14">
              {SECTIONS.map((section) => (
                <div key={section.title}>
                  <h2 className="font-display font-bold text-xl md:text-2xl text-ink-950 mb-4">
                    {section.title}
                  </h2>
                  {section.blocks.map((block, i) => (
                    <SectionBlock key={i} block={block} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
